import type { UserInfo, UserType } from "@/types/api";

export type DashboardRole =
  | "admin"
  | "astrologer"
  | "customer"
  | "perennial"
  | "mystery"
  | "diviner"
  | "affiliate";

interface DashboardConfig {
  key: DashboardRole;
  path: string;
  label: string;
  matchUserTypes?: UserType[];
  matchFlag?: keyof UserInfo;
}

// Order matters: getDefaultDashboard returns the first match.
// Flag-based specialized dashboards (mystery, diviner, perennial) are checked
// BEFORE the generic customer dashboard, matching the Angular auth.service.ts
// priority (is_mystery_school check runs before user_type check).
const DASHBOARD_CONFIGS: DashboardConfig[] = [
  {
    key: "admin",
    path: "/admin/dashboard",
    label: "Admin Dashboard",
    matchUserTypes: ["is_admin"],
  },
  {
    key: "astrologer",
    path: "/admin/astrologer",
    label: "Astrologer Dashboard",
    matchUserTypes: ["is_astrologer", "is_tarotreader", "is_astrologer_tarotreader"],
  },
  {
    key: "perennial",
    path: "/admin/perennial",
    label: "Perennial Mandalism",
    matchUserTypes: ["is_Perennial_Mandalism"],
    matchFlag: "is_perennial_mandalism",
  },
  {
    key: "mystery",
    path: "/admin/mystery-school",
    label: "Mystery School",
    // Identified purely by flag — no dedicated user_type in the API
    matchFlag: "is_mystery_school",
  },
  {
    key: "diviner",
    path: "/admin/diviner",
    label: "Diviner Dashboard",
    // Identified purely by flag — no dedicated user_type in the API
    matchFlag: "is_diviner",
  },
  {
    key: "customer",
    path: "/admin/customer",
    label: "Customer Dashboard",
    // Comes after specialized dashboards so flag checks take priority
    matchUserTypes: ["is_customer", "is_social_advo", "is_customer_socialadvo"],
  },
  {
    key: "affiliate",
    path: "/admin/affiliate",
    label: "Affiliate Dashboard",
    matchUserTypes: ["is_affiliate"],
  },
];

export function getAvailableDashboards(user: UserInfo): DashboardConfig[] {
  return DASHBOARD_CONFIGS.filter((config) => {
    if (config.matchUserTypes?.includes(user.user_type)) return true;
    if (config.matchFlag && (user[config.matchFlag] as number) === 1) return true;
    return false;
  });
}

export function getDefaultDashboard(user: UserInfo): string {
  const dashboards = getAvailableDashboards(user);
  return dashboards[0]?.path ?? "/admin/dashboard";
}

export function hasRole(user: UserInfo, role: DashboardRole): boolean {
  const config = DASHBOARD_CONFIGS.find((c) => c.key === role);
  if (!config) return false;
  if (config.matchUserTypes?.includes(user.user_type)) return true;
  if (config.matchFlag && (user[config.matchFlag] as number) === 1) return true;
  return false;
}

export function isAdmin(user: UserInfo): boolean {
  return user.user_type === "is_admin";
}

export function isAstrologer(user: UserInfo): boolean {
  return ["is_astrologer", "is_tarotreader", "is_astrologer_tarotreader"].includes(user.user_type);
}

export function isCustomer(user: UserInfo): boolean {
  return ["is_customer", "is_social_advo", "is_customer_socialadvo"].includes(user.user_type);
}

export { DASHBOARD_CONFIGS };
